import {
  AuthType,
  CachedImplicitRedirect,
  getErrorAsString,
  HttpResponseError,
  IGetUserInfo,
  MsaImplicitAuthScope,
  WHITELISTED_GRAPH_SCOPES,
  WHITELISTED_OFFICE_SUBSTRATE_SCOPES,
  WHITELISTED_OUTLOOK_OFFICE_SCOPES,
  WhiteListedScopesBySite
} from '@notes/webintegrations-common'
import { createGetUserInfo } from '../shared/auth/authLibFactories'
import { GetTokenFactory } from '../shared/auth/GetTokenFactory'
import { InitialTokenFetchActivity } from '../shared/auth/InitialTokenFetchActivity'
import { implicitAuthCache } from '../shared/caches'
import { Log } from '../shared/logger/Log'
import { IUserFlowInformation } from './IUserFlowInformation'

const MSA_IMPLICIT: AuthType = 'msa_implicit'
const MSA_SCOPES_GROUPED_BY_SITE: WhiteListedScopesBySite[] = [
  WHITELISTED_OFFICE_SUBSTRATE_SCOPES,
  WHITELISTED_OUTLOOK_OFFICE_SCOPES,
  WHITELISTED_GRAPH_SCOPES,
]

const cachedImplicitRedirect = new CachedImplicitRedirect(implicitAuthCache, false)

// Performs msa_implicit auth flow:
// 1) Assuming the user has logged in and we've identified that the user is msa_implicit
// 2) Gets all tokens this app requires
// 3) Gets IAuthenticatedUserInfo and returns it
export async function msaImplicitAuthFlow(activity: InitialTokenFetchActivity): Promise<IUserFlowInformation> {
  Log.logger.info('msa_implicit detected, performing implicit grant exchange token if needed')
  const stopExecution = handleImplicitGrantExchangeToken()
  if (stopExecution) {
    activity.finishWithSuccess(MSA_IMPLICIT, 'ImplicitRedirect')
    return stopExecution
  }

  const tokenGetter = GetTokenFactory.createMsa(false /* forceRefresh */)
  const tokenForUserInfo = await tokenGetter.getGraphUserInfoToken()

  try {
    const userInfoGetter: IGetUserInfo = createGetUserInfo(MSA_IMPLICIT)
    const userInfo = await userInfoGetter.getUserInfo(
      tokenForUserInfo,
      MSA_IMPLICIT
    )
    activity.finishWithSuccess(userInfo.authType, 'ObtainedUser')
    return {
      shouldStopExecution: false,
      userInfo: userInfo,
    }
  } catch (error) {
    Log.logger.error(`Failed to get user information for msa_implicit. Showing the "reload the page" banner and clearing graph token. ${getErrorAsString(error)}`)
    cachedImplicitRedirect.removeFromCache(MsaImplicitAuthScope.GraphUserRead)

    if (HttpResponseError.isHttpResponseError(error) && error.responseCode === 401) {
      const isRedirecting = cachedImplicitRedirect.redirectIfNotInCache(WHITELISTED_GRAPH_SCOPES)
      if (isRedirecting) {
        activity.finishWithFailure(MSA_IMPLICIT, 'Unexpected401WhenObtainingUserRedirect', error)
        Log.logger.info('Redirecting to forcefully refresh Graph scopes')
        return {
          shouldStopExecution: true,
          userInfo: undefined,
        }
      } else {
        activity.finishWithFailure(MSA_IMPLICIT, 'Unexpected401WhenObtainingUserButUnableToRedirect', error)
        Log.logger.warn('Unexpected: not redirecting after forcefully removing Graph scopes')
        return {
          shouldStopExecution: false,
          userInfo: undefined,
        }
      }
    }

    activity.finishWithFailure(MSA_IMPLICIT, 'FailedToObtainUser', error)
    return {
      shouldStopExecution: false,
      userInfo: undefined,
    }
  }
}

// Handles redirection in this window to exchange tokens using implicit (non-silent) grant
function handleImplicitGrantExchangeToken(): IUserFlowInformation | undefined {
  const fragmentCaptured = cachedImplicitRedirect.captureRedirectFragmentInCache()
  if (fragmentCaptured) {
    Log.logger.info('Implicit credentials detected in fragment and was captured in the cache')
  }

  for (const siteScopes of MSA_SCOPES_GROUPED_BY_SITE) {
    const isRedirecting = cachedImplicitRedirect.redirectIfNotInCache(
      siteScopes
    )
    if (isRedirecting) {
      Log.logger.info(`Redirecting as credentials for scopes ${JSON.stringify(siteScopes)} are missing or expired`)
      return {
        shouldStopExecution: true,
        userInfo: undefined,
      }
    }
  }

  Log.logger.info('No implicit grant redirect needed. Starting app normally.')
  return undefined
}
